/**
 * Marketing brand twin helpers (Inbound GPT @ easygpt.builders vs SiteGlue AI @ siteglue.ai).
 * Cross-domain auth cannot share cookies — pass `brand=siteglue` on all SiteGlue → app links.
 */

export const MARKETING_BRAND_QUERY_KEY = "brand";
export const MARKETING_BRAND_SITEGLUE = "siteglue";
export const MARKETING_BRAND_SESSION_KEY = "eg_marketing_brand";
/** Cookie so middleware can rewrite /favicon.ico on app.easygpt.builders. */
export const MARKETING_BRAND_COOKIE_KEY = "eg_marketing_brand";

export type MarketingBrandId = "siteglue" | "inbound_gpt";

export const SITEGLUE_FAVICON_PNG = "/siteglue-favicon-32.png";
export const SITEGLUE_FAVICON_SVG = "/siteglue/favicon.svg";
export const INBOUND_GPT_FAVICON = "/favicon.ico";

export function isSiteGlueMarketingHost(hostname: string): boolean {
  const host = hostname.split(":")[0]?.toLowerCase() ?? "";
  return host === "siteglue.ai" || host === "www.siteglue.ai";
}

export function isEasyGPTMarketingHost(hostname: string): boolean {
  const host = hostname.split(":")[0]?.toLowerCase() ?? "";
  return host === "easygpt.builders" || host === "www.easygpt.builders";
}

/** Marketing hosts that should never serve Clerk auth UI (redirect to app). */
export function isMarketingAuthRedirectHost(hostname: string): boolean {
  return isEasyGPTMarketingHost(hostname) || isSiteGlueMarketingHost(hostname);
}

export function getMarketingBrandFromHostname(hostname: string): MarketingBrandId {
  if (isSiteGlueMarketingHost(hostname)) return "siteglue";
  return "inbound_gpt";
}

export function parseMarketingBrandParam(
  value: string | null | undefined,
): MarketingBrandId | null {
  const normalized = value?.trim().toLowerCase();
  if (normalized === MARKETING_BRAND_SITEGLUE) return "siteglue";
  if (normalized === "inbound_gpt" || normalized === "easygpt") return "inbound_gpt";
  return null;
}

/** Persist brand on the app host after navigating from marketing twins. */
export function persistMarketingBrand(brand: MarketingBrandId | null | undefined): void {
  if (typeof window === "undefined") return;
  try {
    if (brand === "siteglue") {
      sessionStorage.setItem(MARKETING_BRAND_SESSION_KEY, MARKETING_BRAND_SITEGLUE);
      document.cookie = `${MARKETING_BRAND_COOKIE_KEY}=${MARKETING_BRAND_SITEGLUE}; Path=/; Max-Age=${60 * 60 * 24 * 30}; SameSite=Lax`;
    } else if (brand === "inbound_gpt") {
      // Explicit Inbound GPT (e.g. Sign In from easygpt.builders) must clear a
      // prior SiteGlue session so /sign-in does not keep SiteGlue chrome/org routing.
      sessionStorage.removeItem(MARKETING_BRAND_SESSION_KEY);
      document.cookie = `${MARKETING_BRAND_COOKIE_KEY}=; Path=/; Max-Age=0; SameSite=Lax`;
    }
  } catch {
    /* ignore */
  }
}

/**
 * Sync document title for SiteGlue sessions on app.easygpt.builders.
 * IMPORTANT: Do NOT remove/replace <link rel="icon"> nodes — that races React's
 * head manager and throws removeChild TypeErrors (widget iframe + post-login 500 UI).
 * Favicon for SiteGlue marketing hosts is handled by root layout + middleware rewrite.
 */
export function applyMarketingBrandDocumentChrome(brand: MarketingBrandId): void {
  if (typeof document === "undefined") return;
  document.title = brand === "siteglue" ? "SiteGlue" : "Inbound GPT";
  if (brand === "siteglue") {
    document.documentElement.setAttribute("data-marketing-brand", "siteglue");
  } else {
    document.documentElement.removeAttribute("data-marketing-brand");
  }
}

export function readPersistedMarketingBrand(): MarketingBrandId | null {
  if (typeof window === "undefined") return null;
  try {
    return parseMarketingBrandParam(sessionStorage.getItem(MARKETING_BRAND_SESSION_KEY));
  } catch {
    return null;
  }
}

/**
 * Resolve marketing brand from URL search, session, or current hostname.
 * Call on the client after landing on app.easygpt.builders.
 */
export function resolveClientMarketingBrand(
  searchParams?: URLSearchParams | null,
): MarketingBrandId {
  const fromQuery = parseMarketingBrandParam(searchParams?.get(MARKETING_BRAND_QUERY_KEY));
  if (fromQuery) {
    persistMarketingBrand(fromQuery);
    return fromQuery;
  }
  const persisted = readPersistedMarketingBrand();
  if (persisted) return persisted;
  if (typeof window !== "undefined") {
    return getMarketingBrandFromHostname(window.location.hostname);
  }
  return "inbound_gpt";
}

/** Append or replace `brand=` on an absolute or relative URL. */
export function withMarketingBrandParam(
  href: string,
  brand: MarketingBrandId | null | undefined,
): string {
  if (brand !== "siteglue") return href;
  try {
    const base =
      typeof window !== "undefined" ? window.location.origin : "https://app.easygpt.builders";
    const url = new URL(href, base);
    url.searchParams.set(MARKETING_BRAND_QUERY_KEY, MARKETING_BRAND_SITEGLUE);
    // Preserve relative hrefs when input was relative
    if (href.startsWith("/")) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.href;
  } catch {
    const sep = href.includes("?") ? "&" : "?";
    return `${href}${sep}${MARKETING_BRAND_QUERY_KEY}=${MARKETING_BRAND_SITEGLUE}`;
  }
}

export function siteglueBrandQuery(): Record<string, string> {
  return { [MARKETING_BRAND_QUERY_KEY]: MARKETING_BRAND_SITEGLUE };
}
